import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useDaywiseTotalEnergy(range = null) {
    const [energyData, setEnergyData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const { send, isConnected, isOpen, connect, registerHandler } = useWebSocketContext();
    const mountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    const groupId = localStorage.getItem("group_id");
    const roleId = localStorage.getItem("role_id");

    // Default 30-day range
    const defaultRange = () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 29);
        const format = (d) => d.toISOString().split("T")[0];
        return { start_date: format(start), end_date: format(end) };
    };

    // Helper to normalize range object into {start_date, end_date}
    const normalizeRange = (r) => {
        if (!r) return defaultRange();

        if (r.start_date && r.end_date) {
            return r;
        }
        if (r.fromDate && r.toDate) {
            return { start_date: r.fromDate, end_date: r.toDate };
        }
        return defaultRange();
    };

    const fetchEnergyData = useCallback(
        async (attempt = 0) => {
            if (!mountedRef.current) return;

            setIsLoading(true);
            setError('');
            setMessage('');

            try {
                if (retryTimeoutRef.current) {
                    clearTimeout(retryTimeoutRef.current);
                    retryTimeoutRef.current = null;
                }

                if (!isConnected) {
                    await connect();
                    await new Promise((resolve, reject) => {
                        const start = Date.now();
                        const checkInterval = setInterval(() => {
                            if (!mountedRef.current) {
                                clearInterval(checkInterval);
                                reject(new Error('Component unmounted'));
                            } else if (isOpen()) {
                                clearInterval(checkInterval);
                                resolve();
                            } else if (Date.now() - start > 5000) {
                                clearInterval(checkInterval);
                                reject(new Error('Connection timeout'));
                            }
                        }, 100);
                    });
                }

                const appliedRange = normalizeRange(range);

                // Log what you're sending for debugging
                console.log('Daywise Total Energy request:', {
                    event: 'cms_get_daywise_total_energy',
                    payload: {
                        group_id: groupId,
                        role_id: roleId,
                        start_date: appliedRange.start_date,
                        end_date: appliedRange.end_date,
                    },
                });

                const response = await send('cms_get_daywise_total_energy', {
                    group_id: groupId,
                    role_id: roleId,
                    start_date: appliedRange.start_date,
                    end_date: appliedRange.end_date,
                });

                console.log('Daywise Total Energy response:', response);

                if (!response || response.type !== 'cms_get_daywise_total_energy_response') {
                    throw new Error('Invalid response from server');
                }

                if (!Array.isArray(response.data)) {
                    throw new Error('Server did not return a valid energy data list');
                }

                setEnergyData(response.data);
                setMessage(response.message || '');
            } catch (err) {
                setError(err.message);

                if (mountedRef.current) {
                    const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
                    retryTimeoutRef.current = setTimeout(() => {
                        fetchEnergyData(attempt + 1);
                    }, delay);
                }
            } finally {
                if (mountedRef.current) setIsLoading(false);
            }
        },
        [isConnected, isOpen, connect, send, groupId, roleId, range]
    );

    useEffect(() => {
        mountedRef.current = true;
        fetchEnergyData();

        const unregister = registerHandler('daywise_total_energy_update', (msg) => {
            if (Array.isArray(msg.data) && mountedRef.current) {
                setEnergyData(msg.data);
            }
        });

        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            unregister();
        };
    }, [fetchEnergyData, registerHandler]);

    return { energyData, isLoading, error, message, refetch: () => fetchEnergyData(0) };
}
